import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetServiceById, GetServiceItems, AddServiceItem, UpdateServiceItem, DeleteServiceItem,
  GetServicePayments, AddServicePayment, DeleteServicePayment, GetServiceItemTypes, UpdateService,
} from '../../api/api';
import { normaliseText, todayISO } from '../../utils/helpers';

export const PAYMENT_MODES = ['Cash', 'UPI', 'Bank', 'Cheque'];
export const OTHER_ITEM = 'Other';

const blankItem = () => ({ ItemTypeId: '', ItemName: '', ItemGroup: '', Quantity: '1', Rate: '', Remarks: '' });
const blankPayment = () => ({ PaymentDate: todayISO(), Amount: '', PaymentMode: 'Cash', Reference: '', Remarks: '' });

// One engagement in detail: its dynamic lines (menu dishes / jobs / songs — the
// same structure whatever the service is) and its payments. Shared by the
// desktop and mobile views; `vendorView` hides the money-editing controls.
export function useServiceDetail() {
  const { serviceId } = useParams();
  const nav = useNavigate();
  const { user, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  const [service, setService] = useState(null);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemForm, setItemForm] = useState(blankItem);
  const [payForm, setPayForm] = useState(blankPayment);
  const [savingItem, setSavingItem] = useState(false);
  const [savingPay, setSavingPay] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  // A vendor sees his own engagement read-only (except ticking jobs done).
  const isVendor = !!service && String(service.VendorUserId) === String(me) && !isAdmin;
  const canEdit = isAdmin || can('CanServicesEdit') || can('CanServices');
  const canMoney = canEdit && !isVendor;

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      GetServiceById({ ServiceId: serviceId }),
      GetServiceItems({ ServiceId: serviceId }),
      GetServicePayments({ ServiceId: serviceId }),
    ]).then(([svc, its, pays]) => {
      const row = Array.isArray(svc) ? svc[0] : svc;
      setService(row || null);
      setItems(Array.isArray(its) ? its : []);
      setPayments(Array.isArray(pays) ? pays : []);
      if (row?.ServiceTypeId) {
        GetServiceItemTypes({ ServiceTypeId: row.ServiceTypeId })
          .then(t => setItemTypes(Array.isArray(t) ? t : [])).catch(() => setItemTypes([]));
      }
    }).catch(() => toast('Failed to load the service')).finally(() => setLoading(false));
  }, [serviceId, toast]);

  useEffect(() => { load(); }, [load]);

  // "Menu" for a caterer, "Job" for an event manager … comes from the master.
  const itemLabel = service?.ItemLabel || (service?.ServiceType === 'Catering' ? 'Menu' : 'Item');
  const itemOptions = useMemo(() => [
    ...itemTypes.map(t => ({ value: String(t.ItemTypeId), label: t.ItemGroup ? `${t.ItemTypeName} (${t.ItemGroup})` : t.ItemTypeName })),
    { value: OTHER_ITEM, label: 'Other (type a new one)' },
  ], [itemTypes]);

  // Lines grouped by their course / section, in master order.
  const itemsByGroup = useMemo(() => {
    const map = new Map();
    items.forEach(it => {
      const key = it.ItemGroup || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    });
    return Array.from(map.entries()).map(([group, list]) => ({ group, list }));
  }, [items]);

  const itemsTotal = useMemo(() => items.reduce((s, i) => s + Number(i.Amount || 0), 0), [items]);
  const paidTotal = useMemo(() => payments.reduce((s, p) => s + Number(p.Amount || 0), 0), [payments]);

  const setItemF = (k, v) => setItemForm(f => {
    const next = { ...f, [k]: ['ItemName', 'ItemGroup'].includes(k) ? normaliseText(v) : v };
    if (k === 'ItemTypeId' && v !== OTHER_ITEM) {
      const t = itemTypes.find(x => String(x.ItemTypeId) === String(v));
      next.ItemName = t?.ItemTypeName || '';
      next.ItemGroup = t?.ItemGroup || '';
    }
    if (k === 'ItemTypeId' && v === OTHER_ITEM) next.ItemName = '';
    return next;
  });
  const setPayF = (k, v) => setPayForm(f => ({ ...f, [k]: v }));

  const addItem = async () => {
    const name = itemForm.ItemTypeId === OTHER_ITEM ? String(itemForm.ItemName || '').trim() : itemForm.ItemName;
    if (!name) return toast(`Pick or type the ${itemLabel.toLowerCase()}`);
    setSavingItem(true);
    try {
      await AddServiceItem({
        ServiceId: service.ServiceId, EventId: service.EventId,
        ItemTypeId: itemForm.ItemTypeId === OTHER_ITEM ? null : (itemForm.ItemTypeId || null),
        ItemName: name, ItemGroup: itemForm.ItemGroup || null,
        Quantity: Number(itemForm.Quantity || 1), Rate: Number(itemForm.Rate || 0),
        Amount: Number(itemForm.Quantity || 1) * Number(itemForm.Rate || 0),
        Remarks: itemForm.Remarks || null, Status: 'Pending',
        Valid: 1, CreatedBy: me, UpdatedBy: me,
      });
      setItemForm(blankItem());
      load();
    } catch (err) {
      toast(err?.response?.data?.error || 'Could not add');
    } finally { setSavingItem(false); }
  };

  // Jobs tick Done / Pending; menu lines can use it too (e.g. dish finalised).
  const toggleItemStatus = async (row) => {
    const Status = row.Status === 'Done' ? 'Pending' : 'Done';
    setItems(list => list.map(i => (i.ServiceItemId === row.ServiceItemId ? { ...i, Status } : i)));
    try {
      await UpdateServiceItem({ data: { Status, UpdatedBy: me }, where: `ServiceItemId = ${row.ServiceItemId}` });
    } catch { toast('Could not update'); load(); }
  };

  const removeItem = async (row) => {
    if (!window.confirm(`Remove ${row.ItemName}?`)) return;
    try {
      await DeleteServiceItem({ where: `ServiceItemId = ${row.ServiceItemId}` });
      load();
    } catch { toast('Could not remove'); }
  };

  const addPayment = async () => {
    if (!Number(payForm.Amount)) return toast('Enter the amount');
    setSavingPay(true);
    try {
      await AddServicePayment({
        ServiceId: service.ServiceId, EventId: service.EventId,
        PaymentDate: payForm.PaymentDate || todayISO(), Amount: Number(payForm.Amount),
        PaymentMode: payForm.PaymentMode || 'Cash', Reference: payForm.Reference || null,
        Remarks: payForm.Remarks || null, Valid: 1, CreatedBy: me, UpdatedBy: me,
      });
      setPayForm(blankPayment());
      setPayOpen(false);
      toast('Payment recorded');
      load();
    } catch (err) {
      toast(err?.response?.data?.error || 'Could not save the payment');
    } finally { setSavingPay(false); }
  };

  const removePayment = async (row) => {
    if (!window.confirm(`Remove the payment of ${row.Amount}?`)) return;
    try {
      await DeleteServicePayment({ where: `PaymentId = ${row.PaymentId}` });
      load();
    } catch { toast('Could not remove'); }
  };

  // The vendor confirms the plate count he will cook for.
  const setConfirmedCount = async (count) => {
    try {
      await UpdateService({
        data: { ConfirmedCount: Number(count || 0), UpdatedBy: me },
        where: `ServiceId = ${service.ServiceId}`,
      });
      toast('Count updated');
      load();
    } catch { toast('Could not update the count'); }
  };

  const back = () => nav(-1);

  return {
    service, items, itemsByGroup, payments, loading, itemLabel, itemOptions, itemTypes,
    itemsTotal, paidTotal, isVendor, canEdit, canMoney,
    itemForm, setItemF, addItem, savingItem, toggleItemStatus, removeItem,
    payForm, setPayF, addPayment, savingPay, removePayment, payOpen, setPayOpen,
    setConfirmedCount, back,
  };
}
